'use client';

import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { useCallback, useMemo } from 'react';

export type PanelType = 'goals' | 'region' | 'render' | null;

interface UsePanelStateReturn {
  panel: PanelType;
  setPanel: (newPanel: PanelType) => void;
  togglePanel: (panelType: PanelType) => void;
  isOpen: (type: PanelType) => boolean;
  closePanel: () => void;
}

/**
 * Hook for managing panel state via URL query parameters.
 * Uses shallow routing (no page reload) to update URL.
 *
 * @example
 * const { panel, setPanel, togglePanel, isOpen } = usePanelState();
 *
 * // Check if goals panel is open
 * if (isOpen('goals')) { ... }
 *
 * // Open render settings panel
 * setPanel('render');
 *
 * // Toggle panel (open if closed, close if open)
 * togglePanel('goals');
 */
export function usePanelState(): UsePanelStateReturn {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const panel = (searchParams.get('panel') as PanelType) || null;

  const setPanel = useCallback(
    (newPanel: PanelType) => {
      const params = new URLSearchParams(searchParams.toString());

      if (newPanel) {
        params.set('panel', newPanel);
      } else {
        params.delete('panel');
      }

      const queryString = params.toString();
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

      // Use replace with scroll: false for SPA-like behavior
      router.replace(newUrl, { scroll: false });
    },
    [pathname, router, searchParams]
  );

  const togglePanel = useCallback(
    (panelType: PanelType) => {
      setPanel(panel === panelType ? null : panelType);
    },
    [panel, setPanel]
  );

  const isOpen = useCallback((type: PanelType) => panel === type, [panel]);

  const closePanel = useCallback(() => {
    setPanel(null);
  }, [setPanel]);

  return useMemo(
    () => ({
      panel,
      setPanel,
      togglePanel,
      isOpen,
      closePanel,
    }),
    [panel, setPanel, togglePanel, isOpen, closePanel]
  );
}
